/**
 * PURE policy layer for the inline keyboard guard — the centring law, the
 * reveal plan math, the keyboard-height prediction, and the surface gate.
 * No DOM access anywhere in this module (localStorage via keyboardHeightStore
 * is the one I/O, guarded + best-effort), so every rule is exhaustively
 * unit-testable without a device. The DOM installer lives in
 * `inlineKeyboardGuard.js`, which re-exports everything here for call-site
 * compatibility. Extracted 2026-08-21 (500-line cap).
 */

import { loadPersistedKeyboardHeight, loadLegacyKeyboardHeight, persistKeyboardHeight } from './keyboardHeightStore';

/**
 * The guard operates ONLY inside the real app shells. Auth/standalone pages
 * (login, reset-password, accept-invite, unsubscribe) have their own
 * bounded-card layout contract (loginAuthTheme) and are normal-flow
 * documents where iOS's focus pan is native, harmless page behavior — the
 * guard revealing there scrolled the centered auth card (logo pushed
 * off-screen) and padded it 248px (founder capture, 2026-08-21:
 * `path=/ ft=437 st=0 max=0 sp=248`). Allowlist, not blocklist, so any new
 * auth surface is safe by default; `/jobs` + `/log-job` are the two
 * app surfaces that render outside `/app` (no AppShell, but still the
 * fixed-shell layout the pan breaks).
 */
const GUARDED_SURFACE_RE = /^\/(app|platform|jobs|log-job)(\/|$)/;

/** Pure (exported for tests): should the guard operate on this pathname? */
export function isGuardedSurfacePath(pathname) {
  return GUARDED_SURFACE_RE.test(pathname || '');
}

/**
 * Extra raise past the exact centring line. Two jobs: absorb sub-pixel
 * rounding, and absorb iOS's TWO-STAGE keyboard opens — email fields open
 * the plain keyboard first, then grow it ~27px (device capture 2026-08-21:
 * `vv.resize h=474` then `h=447` at +223ms), which moves the centring line
 * ~13.5px UP and re-runs the pan law against the new geometry. 20px keeps
 * the field above the line through the second stage.
 */
export const INLINE_REVEAL_SAFETY_PX = 20;

/**
 * Upper bound on the temporary bottom padding we're willing to add to a
 * scroller to manufacture the scroll range a reveal needs. Sized to a tall
 * iPhone keyboard; beyond this something is wrong with the measurement and we
 * would rather under-reveal than balloon a container.
 */
export const INLINE_REVEAL_MAX_SPACER_PX = 480;

/**
 * Breathing room kept above the field when the reveal is headroom-limited, so
 * a field raised as far as it can go isn't jammed flush against the top edge
 * of its own scroll container.
 */
export const INLINE_REVEAL_TOP_INSET_PX = 8;

/**
 * Cold-start keyboard-height guess, used ONLY before any real measurement
 * exists (first-ever focus in a fresh storage container — later launches load
 * the persisted real value). Deliberately GENEROUS: predicting the keyboard
 * TALLER than reality raises the field a bit higher than needed (harmless —
 * still above the centring line, pan stays 0), while predicting it shorter
 * under-reveals and iOS pans the difference (the residual first-focus lurch
 * the founder reported in the PWA, whose keyboard IS taller than Safari's
 * 322px measurement — Safari's own bottom bar overlaps part of the band).
 */
const DEFAULT_KEYBOARD_HEIGHT_PX = 360;

/**
 * Keyboard KIND. iOS raises very different heights for the number pad
 * (inputmode numeric/decimal/tel, type tel) and the full letter
 * keyboard + suggestion bar (everything else). One height per viewport let the
 * last-opened kind overwrite the other: after a number pad the TALL letter
 * keyboard was predicted ~40px too low and the field fell short of the
 * centring line (device capture, Registrar credito: Monto then Nota).
 * Pure: reads only properties of the element it is handed.
 */
export const KEYBOARD_KINDS = ['numeric', 'text'];
const NUMERIC_INPUT_MODES = new Set(['numeric', 'decimal', 'tel']);
// `type="number"` WITHOUT inputmode shows the FULL keyboard on iOS → text.
const NUMERIC_TYPES = new Set(['tel']);

export function keyboardKindFor(el) {
  if (!el) return 'text';
  let mode = '';
  try {
    mode = String(el.inputMode || (el.getAttribute && el.getAttribute('inputmode')) || '').toLowerCase();
  } catch { /* unreadable → text */ }
  if (NUMERIC_INPUT_MODES.has(mode)) return 'numeric';
  return NUMERIC_TYPES.has(String(el.type || '').toLowerCase()) ? 'numeric' : 'text';
}

/**
 * In-memory cache of REAL measured keyboard heights (fed by
 * `useVisualViewportFrame` once the visualViewport reports one), keyed by the
 * layout-viewport height AND keyboard kind so each orientation/window shape
 * learns its own keyboards. Seeded lazily from the previous launch's persisted
 * measurement (`keyboardHeightStore`), so the generous default above only ever
 * matters for a viewport shape's very first session.
 */
const measuredKeyboard = new Map(); // `${innerHeight}:${kind}` → px

// Measured (or persisted) height for EXACTLY this viewport + kind. Misses are
// cached as 0 so a prediction never re-parses localStorage twice.
function knownHeight(viewportKey, kind) {
  const k = `${viewportKey}:${kind}`;
  let v = measuredKeyboard.get(k);
  if (v == null) {
    v = (kind === 'legacy' ? loadLegacyKeyboardHeight(viewportKey) : loadPersistedKeyboardHeight(viewportKey, kind)) || 0;
    measuredKeyboard.set(k, v);
  }
  return v || null;
}

/** Feed a real measured keyboard height into the predictor. No-op on bad input. */
export function recordKeyboardHeight(h, kind = 'text') {
  if (typeof h !== 'number' || !Number.isFinite(h) || h <= 0) return;
  if (typeof window === 'undefined') return;
  const key = Math.round(window.innerHeight);
  const k = `${key}:${kind}`;
  if (measuredKeyboard.get(k) === h) return;
  measuredKeyboard.set(k, h);
  persistKeyboardHeight(key, h, kind);
}

/**
 * Predicted top edge (viewport Y) of the keyboard band, given the layout
 * viewport height and (optionally) the focused field's keyboard kind.
 * Own kind known → that. Kind unknown → max(default, any known kind): predicting
 * TALL only raises the field a bit more; predicting short makes iOS pan.
 * Kind omitted → the tallest known keyboard (default when none is known yet).
 */
export function predictKeyboardTop(innerHeight, kind) {
  const key = Math.round(innerHeight);
  if (kind) {
    const own = knownHeight(key, kind);
    if (own) return innerHeight - own;
  }
  // A legacy unkeyed entry is a height of UNKNOWN kind: it may only widen the
  // "tallest known" fallback, never stand in as a kind's measured value.
  const known = [...KEYBOARD_KINDS.map((k) => knownHeight(key, k)), knownHeight(key, 'legacy')].filter(Boolean);
  const tallest = known.length ? Math.max(...known) : DEFAULT_KEYBOARD_HEIGHT_PX;
  return innerHeight - (kind ? Math.max(DEFAULT_KEYBOARD_HEIGHT_PX, tallest) : tallest);
}

/**
 * Pure decision (no DOM): the new `scrollTop` (and any temporary padding
 * needed to reach it) for the field's nearest scrollable ancestor.
 *
 * THE LAW (device-derived 2026-08-21, five independent captures, sub-pixel):
 *
 *   iOS CENTRES the focused field in the band the keyboard leaves behind.
 *   pan = fieldCentre − visibleHeight / 2
 *
 * | surface      | field centre | − pan  | lands at | vv.height/2 |
 * | Cobrar       | 312.6        | 75.7   | 236.9    | 237         |
 * | Estimates    | 303.6        | 66.7   | 236.9    | 237         |
 * | Add customer | 282.4        | 45.0   | 237.4    | 237         |
 *
 * It also retro-predicts the PRE-fix captures: Cobrar's 105.7px pan implies a
 * centre of 342.7, and the post-fix log measures the pre-scroll centre at
 * 342.6. And each reveal reduced the pan by EXACTLY the pixels it raised
 * (Cobrar 105.7→75.7 on a 30px raise; Estimates 114.7→66.7 on 48).
 *
 * So the target is not "some clearance below the field" — it is a fixed point:
 * get the field's CENTRE to `predictedKeyboardTop / 2` and iOS has no reason to
 * pan. Two earlier revisions guessed at a constant margin (16px, then 160px);
 * both were wrong because the true target scales with viewport height.
 *
 * Returns `null` when the field is already at/above the centring line, or when
 * raising it would push it out of the TOP of its own container (`headroom` —
 * hiding it upward is no improvement over iOS hiding it downward).
 *
 * `spacer` is temporary bottom padding the caller must add to the scroller to
 * make `scrollTop` reachable. Both reported surfaces were RANGE-LIMITED —
 * Cobrar had 30px of scroll for a 106px need, Estimates 48 for 115 — so
 * without it the reveal is capped far short of the line and the pan survives,
 * merely smaller. It is a loan, restored with the scroll. (Under-FILLED
 * containers can eat part of the spacer too — the installer measures the
 * landed scrollTop and grows the spacer by the shortfall; see the guard.)
 *
 * @param {{
 *   fieldTop:number, fieldBottom:number, scrollerTop:number,
 *   scrollerScrollTop:number, scrollerMaxScrollTop:number,
 *   predictedKeyboardTop:number, safetyPx?:number, maxSpacerPx?:number,
 * }} p
 * @returns {{scrollTop:number, spacer:number}|null}
 */
export function computeInlineRevealPlan({
  fieldTop, fieldBottom, scrollerTop, scrollerScrollTop, scrollerMaxScrollTop,
  predictedKeyboardTop, safetyPx = INLINE_REVEAL_SAFETY_PX,
  maxSpacerPx = INLINE_REVEAL_MAX_SPACER_PX,
}) {
  const targetCentre = predictedKeyboardTop / 2;
  const fieldCentre = (fieldTop + fieldBottom) / 2;
  const desired = (fieldCentre - targetCentre) + safetyPx;
  if (desired <= 0) return null; // already at or above the centring line

  const regionTop = Math.max(scrollerTop, 0);
  const headroom = Math.max(0, fieldTop - regionTop - INLINE_REVEAL_TOP_INSET_PX);
  const delta = Math.min(desired, headroom);
  if (delta <= 0) return null; // no room to raise it without hiding it

  const available = Math.max(0, scrollerMaxScrollTop - scrollerScrollTop);
  const spacer = Math.min(Math.max(0, Math.ceil(delta - available)), maxSpacerPx);
  const applied = Math.min(delta, available + spacer);
  if (applied <= 0) return null;

  return { scrollTop: Math.round(scrollerScrollTop + applied), spacer };
}
